/**
 * Lightweight browser/OS/device parser for support ticket metadata.
 *
 * We don't pull in `ua-parser-js` (~20 KB gzipped) — heuristics on
 * `navigator.userAgent` plus `userAgentData` (modern Chrome/Edge) are
 * plenty for "which device is this user on" context attached to a
 * support ticket. The user-agent itself is also forwarded raw so a
 * human can disambiguate edge cases server-side.
 */

function detectBrowser(ua) {
  // Order matters — Edge/Chrome both contain "Chrome" in UA.
  if (/Edg\//.test(ua)) return 'Edge';
  if (/OPR\/|Opera/.test(ua)) return 'Opera';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return 'Chrome';
  if (/Chromium\//.test(ua)) return 'Chromium';
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'Safari';
  if (/MSIE |Trident\//.test(ua)) return 'Internet Explorer';
  return 'Unknown';
}

function detectBrowserVersion(ua, name) {
  const patterns = {
    Edge: /Edg\/([\d.]+)/,
    Opera: /(?:OPR|Opera)\/([\d.]+)/,
    Firefox: /Firefox\/([\d.]+)/,
    Chrome: /Chrome\/([\d.]+)/,
    Chromium: /Chromium\/([\d.]+)/,
    Safari: /Version\/([\d.]+)/,
  };
  const re = patterns[name];
  if (!re) return '';
  const m = ua.match(re);
  return m ? m[1] : '';
}

function detectOS(ua) {
  if (/Windows NT/.test(ua)) return 'Windows';
  if (/Mac OS X/.test(ua) && !/Mobile/.test(ua)) return 'macOS';
  if (/Android/.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Linux/.test(ua)) return 'Linux';
  if (/CrOS/.test(ua)) return 'ChromeOS';
  return 'Unknown';
}

function detectDeviceType(ua) {
  if (/iPad|Tablet|PlayBook|(Android(?!.*Mobile))/i.test(ua)) return 'Tablet';
  if (/Mobi|Android.*Mobile|iPhone|iPod|BlackBerry|Opera Mini/i.test(ua)) return 'Mobile';
  return 'Desktop';
}

export function getClientMeta() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {};
  }
  const ua = navigator.userAgent || '';
  const browser = detectBrowser(ua);
  const version = detectBrowserVersion(ua, browser);
  return {
    device: detectDeviceType(ua),
    browser: version ? `${browser} ${version}` : browser,
    os: detectOS(ua),
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    language: navigator.language || '',
    // Truncate UA — Pydantic also caps at 500 chars server-side.
    user_agent: ua.slice(0, 500),
    referrer: (document.referrer || '').slice(0, 200),
  };
}
